// components/TelegramModal.jsx
//
// Botón + modal del chat de soporte.
//
// Disponible para todos los usuarios autenticados: se monta en LayoutPriv, así
// que el botón está en cualquier pantalla. El admin ve además el panel de
// soporte (la lista de conversaciones), y el resto, su chat con soporte.
import React, { useEffect, useState } from "react";
import TelegramChat from "./TelegramChat";
import TelegramChatWindowAdmin from "./TelegramChatWindowAdmin";
import { useChatSoporte } from "../context/ChatSoporteContext";

const TelegramModal = ({ role, user, userId, socket, isSocketConnected }) => {
  // El estado del panel es compartido (ver context/ChatSoporteContext.jsx):
  // así el item del menú Admin y este botón abren el MISMO chat, y el badge
  // de la cola es el mismo en los dos lados.
  const { panelAbierto, abrirPanel, cerrarPanel, cola, respuestasSinLeer } = useChatSoporte();

  const esAdmin = role === 'admin';

  // La burbuja se puede colapsar hacia el borde derecho para que no tape
  // formularios ni tablas. Es solo visual (no cierra la conversación) y se
  // recuerda entre pantallas/pestañas para no tener que colapsarla de nuevo.
  const [colapsado, setColapsado] = useState(() => {
    try { return localStorage.getItem('soporte_burbuja_colapsada') === '1'; } catch { return false; }
  });
  const cambiarColapsado = (valor) => {
    setColapsado(valor);
    try { localStorage.setItem('soporte_burbuja_colapsada', valor ? '1' : '0'); } catch { /* sin storage */ }
  };

  // ------------------------------------------------------------------
  // ¿Está el chat de soporte ABIERTO en esta pestaña?
  // ------------------------------------------------------------------
  // El panel del admin muestra un punto verde por usuario. Para que ese
  // punto sea verdad, el usuario tiene que avisar cuándo abre y cuándo cierra
  // el chat: tener la app abierta NO es estar en la conversación (si cerró el
  // chat, la respuesta se le queda esperando y no puede figurar como
  // conectado).
  useEffect(() => {
    if (!socket) return undefined;

    if (panelAbierto) {
      socket.emit('chat_conversacion');
    } else {
      socket.emit('chat_cerrar_conversacion');
    }

    // Al desmontar (o al cambiar el socket) se avisa el cierre: si no, el
    // servidor lo tendría como "en la conversación" para siempre.
    return () => {
      try { socket.emit('chat_cerrar_conversacion'); } catch (e) { }
    };
  }, [socket, panelAbierto]);

  // El badge del botón:
  //  - admin: los mensajes de los usuarios que están en la cola (no abrió).
  //  - usuario: las respuestas del soporte que todavía no leyó (si no, la
  //    respuesta queda esperando en silencio y el usuario no se entera).
  const badge = esAdmin ? cola : respuestasSinLeer;

  return (
    <div>
      {/* Botón flotante.
          OJO con la posición: antes estaba en `fixed top-4 right-4`, que es
          EXACTAMENTE donde aparece la burbuja de notificaciones
          (NotificacionesFlotante: `fixed top-4 right-4 z-50`): uno tapaba al
          otro y no se podía llegar al chat.

          Ahora vive en la ESQUINA INFERIOR DERECHA, debajo de la burbuja del
          Chat Inteligente (que quedó en `bottom-[5.5rem] right-5`): las dos
          burbujas forman una columna y el Chat Inteligente va SIEMPRE arriba,
          para que cuando esté disponible para todos (hoy es solo admin) no
          haya que mover nada. */}
      {colapsado ? (
        // Colapsada: una pestaña fina pegada al borde derecho. Al hacer click
        // vuelve a la píldora completa.
        <button
          onClick={() => cambiarColapsado(false)}
          title="Mostrar el chat de soporte"
          aria-label="Mostrar el chat de soporte"
          className="hidden md:flex fixed bottom-4 right-0 z-40 items-center gap-1 pl-2 pr-1.5 py-2 bg-blue-500 text-white rounded-l-full hover:bg-blue-600 shadow-lg"
        >
          <span aria-hidden="true">💬</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          {badge > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-semibold">
              {badge}
            </span>
          )}
        </button>
      ) : (
        <div className="hidden md:flex fixed bottom-4 right-5 z-40 items-stretch rounded-full bg-blue-500 text-white shadow-lg overflow-hidden">
          <button
            onClick={abrirPanel}
            title="Chat de soporte con el administrador"
            className="flex items-center gap-2 px-4 py-2 hover:bg-blue-600"
          >
            <span aria-hidden="true">💬</span>
            <span>{esAdmin ? 'Panel ADMIN' : 'Conectar con ADMIN'}</span>
            {badge > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-semibold">
                {badge}
              </span>
            )}
          </button>
          <button
            onClick={() => cambiarColapsado(true)}
            title="Colapsar hacia la derecha"
            aria-label="Colapsar el chat de soporte"
            className="px-2 border-l border-blue-400/60 hover:bg-blue-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* En mobile el botón es circular (sin texto): abajo a la derecha está
          la barra de GUARDAR/CANCELAR de Inicio, que apila los botones y es de
          ancho completo, así que un botón ancho taparía el CANCELAR. */}
      <button
        onClick={abrirPanel}
        title="Chat de soporte con el administrador"
        className="md:hidden fixed bottom-4 right-4 z-40 w-12 h-12 flex items-center justify-center bg-blue-500 text-white rounded-full hover:bg-blue-600 shadow-lg text-xl relative"
      >
        <span aria-hidden="true">💬</span>
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-semibold">
            {badge}
          </span>
        )}
      </button>

      {panelAbierto && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-5/6 lg:w-2/3 shadow-xl dark:shadow-gray-900/50">
            {esAdmin ? (
              <TelegramChatWindowAdmin user={user} userId={userId} role={role} socket={socket} isSocketConnected={isSocketConnected} />
            ) : (
              <TelegramChat user={user} userId={userId} socket={socket} isSocketConnected={isSocketConnected} />
            )}

            <div className="flex justify-end space-x-4 mt-4">
              <button
                onClick={cerrarPanel}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors dark:bg-red-600 dark:hover:bg-red-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramModal;
